import { ChangeDetectorRef, Component, ElementRef, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ApiSettings, PaginationConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { SentEmailSortOptions } from '../../../../core/enums/sort-options.enums';
import { EmailDeliveryStatus, EmailType } from '../../../../core/enums/data.enums';
import { SentEmailListViewLocations } from '../../../../core/enums/base.enums';
import { SentEmailModel } from '../../../../core/models/data/sent-email.model';
import { SentEmailFilter } from '../../../../core/filters/sent-email.filter';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { SentEmailService } from '../../../../core/services/messaging/sent-email.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { SentEmailsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { FormatLabelPipe } from '../../../../core/pipes/format-label/format-label.pipe';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';

@Component({
  selector: 'ms-sent-email-list-table',
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    PaginationComponent,
    InViewPortDirective,
    NoValuePipe,
    OrgDatePipe,
    FormatLabelPipe,
    StatusBadgeComponent,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    TooltipDirective
  ],
  templateUrl: './sent-email-list-table.component.html',
  styleUrl: './sent-email-list-table.component.scss',
  host: { "style": "display:contents" }
})
export class SentEmailListTableComponent extends BaseListPageClass implements OnInit {

  //#region Properties & Signals
  public filter = input<SentEmailFilter | undefined>(undefined);
  public orderByInput = input<string | undefined>(undefined);
  public viewLocation = input<SentEmailListViewLocations>(SentEmailListViewLocations.OrganizationCommunicationsPage);

  public orderByChanged = output<string>();

  public sentEmails = signal<SentEmailModel[]>([]);
  public sentEmailsFilter: SentEmailFilter = ListConstants.SENT_EMAILS_FILTER_DEFAULT();

  public SentEmailSortOptions = SentEmailSortOptions;
  public SentEmailsMessagingConstants = SentEmailsMessagingConstants;
  public SentEmailListViewLocations = SentEmailListViewLocations;

  public skeletonSentEmails: SentEmailModel[] = [];

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;
  //#endregion Properties & Signals

  constructor(
    private sentEmailService: SentEmailService,
    private changeDetector: ChangeDetectorRef,
    public userSessionService: UserSessionService
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.sortOptions = [...ListConstants.SENT_EMAILS_SORT_OPTIONS()];

    const incomingFilter = this.filter();
    this.sentEmailsFilter = incomingFilter
      ? { ...incomingFilter }
      : ListConstants.SENT_EMAILS_FILTER_DEFAULT();

    this.orderBy = this.orderByInput() ?? ListConstants.SENT_EMAILS_ORDERBY_DEFAULT();

    // The member-profile view is already scoped to a single member (org-agnostic — a
    // member record belongs to exactly one org), so it must NOT be further restricted to
    // the viewer's currently-active organization. Doing so would hide results for Staff
    // viewing a member profile in an org other than their active one (DEV-2428).
    if (this.viewLocation() !== SentEmailListViewLocations.MemberProfilePage
      && this.userSessionService.activeOrganization()) {
      this.sentEmailsFilter.organizationIds = [this.userSessionService.activeOrganization()!.organizationId];
    }

    this.createSkeletonSentEmails();
    await this.loadSentEmails(false);
  }

  private createSkeletonSentEmails(): void {
    this.skeletonSentEmails = Array.from({ length: this.take }, (_, index) => ({
      id: index,
      organizationId: 0,
      recipientEmailAddress: 'loading@example.com',
      subject: 'Loading...',
      emailType: EmailType.InvoiceIssued,
      sentAt: '2025-01-01T00:00:00Z',
      status: EmailDeliveryStatus.Sent
    } as SentEmailModel));
  }

  //#region Data Loading
  public get displaySentEmails(): SentEmailModel[] {
    if (this.isLoading && (!this.sentEmails() || this.sentEmails().length === 0)) {
      return this.skeletonSentEmails;
    }
    return this.sentEmails();
  }

  public get isEmpty(): boolean {
    return !this.isLoading && this.sentEmails().length === 0;
  }

  public async loadSentEmails(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    const incomingFilter = this.filter();
    if (incomingFilter) {
      this.sentEmailsFilter = { ...incomingFilter };
    }

    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.sentEmailsFilter.timeZoneId =
      orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;

    const response: ServiceResultModel<PaginatedListModel<SentEmailModel>> =
      await this.sentEmailService.searchSentEmails(this.sentEmailsFilter, this.orderBy, this.skip(), this.take);

    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append === true) {
        this.sentEmails.set(this.sentEmails().concat(response.data.data));
      } else {
        this.sentEmails.set(response.data.data);
      }
    } else if (append === false) {
      this.sentEmails.set([]);
    }

    this.isLoading = false;
    this.changeDetector.detectChanges();
  }

  public async loadMore(): Promise<void> {
    await this.loadSentEmails(true);
  }

  public filterApplied(): void {
    this.loadSentEmails();
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
  //#endregion Data Loading

  //#region Sorting & Pagination
  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [SentEmailSortOptions, SentEmailSortOptions]> = {
      "Recipient": [SentEmailSortOptions.RecipientAsc, SentEmailSortOptions.RecipientDesc],
      "Subject": [SentEmailSortOptions.SubjectAsc, SentEmailSortOptions.SubjectDesc],
      "Type": [SentEmailSortOptions.EmailTypeAsc, SentEmailSortOptions.EmailTypeDesc],
      "Sent": [SentEmailSortOptions.SentAtAsc, SentEmailSortOptions.SentAtDesc],
      "Status": [SentEmailSortOptions.StatusAsc, SentEmailSortOptions.StatusDesc]
    };

    const [ascOption, descOption] = columnSortMap[columnName]
      || [SentEmailSortOptions.SentAtAsc, SentEmailSortOptions.SentAtDesc];

    let sortDirection: SentEmailSortOptions;

    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.setSortOrder(sortDirection);
  }

  public setSortOrder(sortOption: string) {
    this.orderBy = sortOption;
    this.orderByChanged.emit(sortOption);
    this.filterApplied();
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }
  //#endregion Sorting & Pagination
}
